import React, { useState, useEffect, useRef } from 'react';
import VirtualKeyboard from './VirtualKeyboard';
import metricsTracker from '../utils/metricsTracker';

const Subtraction = ({ updateMetrics }) => {
  const [num1, setNum1] = useState('');
  const [num2, setNum2] = useState('');
  const [userAnswer, setUserAnswer] = useState([]);
  const [borrowValues, setBorrowValues] = useState([]);
  const [remainingValues, setRemainingValues] = useState([]);
  const [afterBorrowingValues, setAfterBorrowingValues] = useState([]);
  const [afterReductionValues, setAfterReductionValues] = useState([]);
  const [strikeoutValues, setStrikeoutValues] = useState([]);
  const [showSolution, setShowSolution] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);
  const [customNumbers, setCustomNumbers] = useState({ num1: '', num2: '' });
  const [useCustomNumbers, setUseCustomNumbers] = useState(false);
  const [gradeLevel, setGradeLevel] = useState('1');
  const [questionNumber, setQuestionNumber] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [solutionSteps, setSolutionSteps] = useState(null);
  
  // Virtual keyboard state
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardPosition, setKeyboardPosition] = useState({ x: 200, y: 100 });
  const [activeInput, setActiveInput] = useState(null);
  
  // Progress tracking state
  const [sessionStats, setSessionStats] = useState({
    totalAttempted: 0,
    totalCorrect: 0,
    totalShownSolutions: 0
  });
  
  // Button state management
  const [buttonsState, setButtonsState] = useState({
    checkAnswerDisabled: false,
    showSolutionDisabled: true
  });
  
  const mathProblemRef = useRef(null);

  // Level difficulty ranges - properly aligned with "Upto X digits"
  const gradeRanges = {
    '1': { min: 1, max: 99 },        // Upto 2 digits
    '2': { min: 10, max: 999 },      // Upto 3 digits
    '3': { min: 100, max: 9999 },    // Upto 4 digits
    '4': { min: 1000, max: 99999 },  // Upto 5 digits
    '5': { min: 10000, max: 999999 }, // Upto 6 digits
    '6': { min: 100000, max: 9999999 }, // Upto 7 digits
    '7': { min: 1000000, max: 99999999 }, // Upto 8 digits
    '8': { min: 10000000, max: 999999999 } // Upto 9 digits
  };

  // Calculate paper savings
  const calculatePaperSavings = () => {
    const linesPerQuestion = 3;
    const validTotalQuestions = Number.isFinite(totalQuestions) ? totalQuestions : 0;
    const totalLines = validTotalQuestions * linesPerQuestion;
    const papersSaved = Math.ceil(totalLines / 25);
    return { papersSaved, totalLines };
  };

  // Calculate the required length for answer
  const calculateAnswerLength = () => {
    const a = parseInt(num1) || 0;
    const b = parseInt(num2) || 0;
    const result = Math.max(a, b);
    return result.toString().length;
  };

  // Generate random numbers for the problem (ensuring num1 >= num2)
  const generateProblem = (isInitialLoad = false) => {
    let finalNum1, finalNum2;
    
    if (useCustomNumbers && customNumbers.num1 && customNumbers.num2) {
      const num1Val = parseInt(customNumbers.num1);
      const num2Val = parseInt(customNumbers.num2);
      
      // Ensure num1 >= num2 for subtraction
      if (num1Val >= num2Val) {
        setNum1(customNumbers.num1);
        setNum2(customNumbers.num2);
        finalNum1 = customNumbers.num1;
        finalNum2 = customNumbers.num2;
      } else {
        setNum1(customNumbers.num2);
        setNum2(customNumbers.num1);
        finalNum1 = customNumbers.num2;
        finalNum2 = customNumbers.num1;
      }
    } else {
      const range = gradeRanges[gradeLevel];
      const newNum1 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
      const newNum2 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
      
      // Ensure num1 >= num2 for subtraction
      if (newNum1 >= newNum2) {
        setNum1(newNum1.toString());
        setNum2(newNum2.toString());
      } else {
        setNum1(newNum2.toString());
        setNum2(newNum1.toString());
      }
    }
    
    setUserAnswer([]);
     // Initialize arrays with the full length needed for the problem
     const maxLength = Math.max(num1.length, num2.length);
     setBorrowValues(new Array(maxLength).fill(''));
     setRemainingValues(new Array(maxLength).fill(''));
     setAfterBorrowingValues(new Array(maxLength).fill(''));
     setAfterReductionValues(new Array(maxLength).fill(''));
     setStrikeoutValues(new Array(maxLength).fill(''));
    setShowSolution(false);
    setSolutionSteps(null);
    setIsCorrect(null);
    setMessage('');
    
    // Reset button states for new problem
    setButtonsState({
      checkAnswerDisabled: false,
      showSolutionDisabled: true
    });
    
    // Only increment question number if it's not the initial load
    if (!isInitialLoad) {
    setQuestionNumber(prev => prev + 1);
    }
    setTotalQuestions(prev => prev + 1);
    
    // Report metrics for new problem
    if (updateMetrics) {
      const { papersSaved } = calculatePaperSavings();
      updateMetrics(papersSaved);
    }
  };

  // Handle digit input changes
  const handleDigitChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newAnswer = [...userAnswer];
      newAnswer[index] = value;
      setUserAnswer(newAnswer);
    }
  };



  // Handle remaining input
  const handleRemainingChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newRemaining = [...remainingValues];
      newRemaining[index] = value;
      setRemainingValues(newRemaining);
    }
  };

  // Handle borrow input (single digit allowed)
  const handleBorrowChange = (index, value) => {
    // Only allow single digit and empty string
    if (value === '' || /^\d{1}$/.test(value)) {
      const newBorrow = [...borrowValues];
      newBorrow[index] = value;
      setBorrowValues(newBorrow);
    }
  };

  // Handle after borrowing input (two digits allowed)
  const handleAfterBorrowingChange = (index, value) => {
    if (value.length <= 2 && /^\d*$/.test(value)) {
      const newAfterBorrowing = [...afterBorrowingValues];
      newAfterBorrowing[index] = value;
      setAfterBorrowingValues(newAfterBorrowing);
    }
  };

  // Handle after reduction input
  const handleAfterReductionChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newAfterReduction = [...afterReductionValues];
      newAfterReduction[index] = value;
      setAfterReductionValues(newAfterReduction);
    }
  };

  // Handle strikeout input
  const handleStrikeoutChange = (index, value) => {
    if (value === '/' || value === '') {
      const newStrikeout = [...strikeoutValues];
      newStrikeout[index] = value;
      setStrikeoutValues(newStrikeout);
    }
  };

  // Handle input focus - show keyboard and position it
  const handleInputFocus = (index, type) => {
    setActiveInput({ index, type });
    setKeyboardVisible(true);
    
    // Position keyboard just next to the input
     const inputElement = document.querySelector(`input[data-index="${index}"][data-type="${type}"]`);
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      const mathProblemRect = mathProblemRef.current?.getBoundingClientRect();
      
      if (mathProblemRect) {
        // Calculate position based on place value
        const maxLength = Math.max(num1.length, num2.length);
        const answerLength = calculateAnswerLength();
        const placeValue = (type === 'answer' ? answerLength : maxLength) - index - 1;
        
        // Base position: just next to input (5px offset)
        let x = rect.right - mathProblemRect.left + 5;
        
        // Move left for higher place values to avoid hiding questions
        if (placeValue >= 1) {
          x -= (placeValue * 15);
        }
        
        // Ensure keyboard doesn't go too far left
        x = Math.max(x, 50);
        
        // Move keyboard down to avoid hiding the second number
        let y = rect.top - mathProblemRect.top;
        
        // If this is a borrow/remaining field, move keyboard down more
        if (type === 'borrow' || type === 'remaining') {
          y += 60;
        } else {
          y += 40;
        }
        
        setKeyboardPosition({ x, y });
      }
    }
  };

  // Handle input blur
  const handleInputBlur = () => {
    // Don't hide keyboard, don't clear active input to allow virtual keyboard input
  };

  // Handle virtual keyboard input
  const handleVirtualKeyPress = (key) => {
    if (activeInput) {
      if (activeInput.type === 'answer') {
        handleDigitChange(activeInput.index, key);
      } else if (activeInput.type === 'borrow') {
        handleBorrowChange(activeInput.index, key);
      } else if (activeInput.type === 'afterBorrowing') {
        handleAfterBorrowingChange(activeInput.index, key);
      } else if (activeInput.type === 'afterReduction') {
        handleAfterReductionChange(activeInput.index, key);
      } else if (activeInput.type === 'strikeout') {
        handleStrikeoutChange(activeInput.index, key);
      }
      
      // Re-focus the active input field after virtual keyboard input
      setTimeout(() => {
        const inputElement = document.querySelector(`input[data-index="${activeInput.index}"][data-type="${activeInput.type}"]`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Handle virtual keyboard backspace
  const handleVirtualBackspace = () => {
    if (activeInput) {
      if (activeInput.type === 'answer') {
        const newAnswer = [...userAnswer];
        newAnswer[activeInput.index] = '';
        setUserAnswer(newAnswer);
      } else if (activeInput.type === 'borrow') {
        const newBorrow = [...borrowValues];
        newBorrow[activeInput.index] = '';
        setBorrowValues(newBorrow);
      } else if (activeInput.type === 'afterBorrowing') {
        const newAfterBorrowing = [...afterBorrowingValues];
        newAfterBorrowing[activeInput.index] = '';
        setAfterBorrowingValues(newAfterBorrowing);
      } else if (activeInput.type === 'afterReduction') {
        const newAfterReduction = [...afterReductionValues];
        newAfterReduction[activeInput.index] = '';
        setAfterReductionValues(newAfterReduction);
      } else if (activeInput.type === 'strikeout') {
        const newStrikeout = [...strikeoutValues];
        newStrikeout[activeInput.index] = '';
        setStrikeoutValues(newStrikeout);
      }
      
      // Re-focus the active input field after virtual keyboard backspace
      setTimeout(() => {
        const inputElement = document.querySelector(`input[data-index="${activeInput.index}"][data-type="${activeInput.type}"]`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Handle virtual keyboard clear
  const handleVirtualClear = () => {
    if (activeInput) {
      if (activeInput.type === 'answer') {
        const newAnswer = [...userAnswer];
        newAnswer[activeInput.index] = '';
        setUserAnswer(newAnswer);
      } else if (activeInput.type === 'borrow') {
        const newBorrow = [...borrowValues];
        newBorrow[activeInput.index] = '';
        setBorrowValues(newBorrow);
      } else if (activeInput.type === 'afterBorrowing') {
        const newAfterBorrowing = [...afterBorrowingValues];
        newAfterBorrowing[activeInput.index] = '';
        setAfterBorrowingValues(newAfterBorrowing);
      } else if (activeInput.type === 'afterReduction') {
        const newAfterReduction = [...afterReductionValues];
        newAfterReduction[activeInput.index] = '';
        setAfterReductionValues(newAfterReduction);
      } else if (activeInput.type === 'strikeout') {
        const newStrikeout = [...strikeoutValues];
        newStrikeout[activeInput.index] = '';
        setStrikeoutValues(newStrikeout);
      }
      
      // Re-focus the active input field after virtual keyboard clear
      setTimeout(() => {
        const inputElement = document.querySelector(`input[data-index="${activeInput.index}"][data-type="${activeInput.type}"]`);
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Validate the answer
  const validateAnswer = () => {
    const correctAnswer = parseInt(num1) - parseInt(num2);
    const userAnswerString = userAnswer.join('');
    const expectedResultString = correctAnswer.toString();
    
    // Check if user has entered any answer
    if (userAnswerString.length === 0) {
      setMessage('Please enter your answer before checking.');
      setIsCorrect(false);
      return; // Don't proceed with validation or tracking
    }
    
    // Always proceed to detailed validation regardless of length
    // This prevents giving away answer length hints to the user
    // Track attempt for any validation attempt
    setSessionStats(prev => ({
      ...prev,
      totalAttempted: prev.totalAttempted + 1
    }));
    
    // Perform detailed validation
    const validationResult = performDetailedValidation();
    
    if (validationResult.isCorrect && validationResult.errors.length === 0) {
      setIsCorrect(true);
      setMessage('Correct! Well done! 🎉');
      setScore(prev => prev + 10);
      
      // Track correct answer
      setSessionStats(prev => ({
        ...prev,
        totalCorrect: prev.totalCorrect + 1
      }));
      
      // Track metrics for correct answer
      metricsTracker.trackCheckAnswer('subtraction', gradeLevel, true);
    } else {
      setIsCorrect(false);
      setMessage(validationResult.message);
      setScore(prev => Math.max(0, prev - 5));
      
      // Track metrics for incorrect answer
      metricsTracker.trackCheckAnswer('subtraction', gradeLevel, false);
    }
    
    // Only disable Check Answer button after validation attempt
    setButtonsState(prev => ({
      ...prev,
      checkAnswerDisabled: true,
      showSolutionDisabled: false // Enable Show Solution after checking
    }));
  };

  // Perform detailed step-by-step validation
  const performDetailedValidation = () => {
    const a = parseInt(num1);
    const b = parseInt(num2);
    const expectedResult = a - b;
    const expectedResultString = expectedResult.toString();
    const userAnswerString = userAnswer.join('');
    
    // Check if user attempted the problem
    if (userAnswerString.length === 0) {
      return {
        isCorrect: false,
        message: 'Please attempt the problem. Enter your answer digit by digit.',
        errors: ['unattempted']
      };
    }
    
    // FIRST: Check if the answer is correct
    const answerCorrect = userAnswerString === expectedResult.toString();
    
    if (answerCorrect) {
      // Answer is correct, now check borrowing fields
      const borrowingValidation = validateBorrowingOnly();
      
      if (borrowingValidation.errors.length === 0) {
        return {
          isCorrect: true,
          message: 'Correct! Well done! 🎉',
          errors: []
        };
      } else {
        // Answer correct but borrowing validation failed
        const errorMessages = borrowingValidation.errors.map((error, index) => {
          // For wrong values, show the specific error message
          if (error.type === 'wrong_borrow' || error.type === 'wrong_after_borrowing' || 
              error.type === 'wrong_after_reduction' || error.type === 'wrong_strikeout') {
            return `${index + 1}. ${error.message}`;
          } else {
            // For missing values, show the standard message
            return `${index + 1}. ${error.message}`;
          }
        });
        
        return {
          isCorrect: true,
          message: `Correct answer! 🎉 However, you need to practice the complete process:\n\n${errorMessages.join('\n')}`,
          errors: borrowingValidation.errors
        };
      }
    } else {
      // Answer is wrong, find the first error starting from ones place
      const answerValidation = validateAnswerStepByStep();
      const errorMessage = generateDetailedErrorMessage(answerValidation);
      
      return {
        isCorrect: false,
        message: errorMessage,
        errors: answerValidation.errors
      };
    }
  };

         // Validate borrowing fields only (when answer is correct) - improved systematic approach
   const validateBorrowingOnly = () => {
     let errors = [];
     
     // Get the current problem numbers and extract digits
     const a = parseInt(num1);
     const b = parseInt(num2);
     
     // Extract digits from right to left (ones place = index 0, tens place = index 1)
     const aDigits = a.toString().split('').reverse();
     const bDigits = b.toString().split('').reverse();
     const maxLength = Math.max(num1.length, num2.length);
     
     // Create a temporary array to track current digit values after borrowing
     let currentDigits = [...aDigits.map(d => parseInt(d || 0))];
     
     // Process each place value from right to left (ones to thousands)
     for (let position = 0; position < maxLength; position++) {
       const digitA = currentDigits[position]; // Use current value (after previous borrowing)
       const digitB = parseInt(bDigits[position] || 0);
       const placeValueName = getPlaceValueName(position);
       
       // Check if this position needs borrowing
       let needsBorrowing = digitA < digitB;
       
       if (needsBorrowing) {
         // STEP 1: Check borrowing value (should be 1)
         if (borrowValues[position] === undefined || borrowValues[position] === '') {
           errors.push({
             type: 'missing_borrow',
             position: position,
             placeValue: placeValueName,
             message: `Missing borrowing in ${placeValueName} place. You need to write 1 in the borrow field.`
           });
         } else if (parseInt(borrowValues[position]) !== 1) {
           errors.push({
             type: 'wrong_borrow',
             position: position,
             placeValue: placeValueName,
             message: `Wrong borrowing value in ${placeValueName} place. You need to write 1, not ${borrowValues[position]}.`
           });
         }
         
         // STEP 2: Check "After borrowing" value
         let expectedAfterBorrowing = digitA + 10;
         
         if (afterBorrowingValues[position] === undefined || afterBorrowingValues[position] === '') {
           errors.push({
             type: 'missing_after_borrowing',
             position: position,
             placeValue: placeValueName,
             message: `Missing "After borrowing" value in ${placeValueName} place. When borrowing, ${digitA} becomes ${expectedAfterBorrowing}.`
           });
         } else if (parseInt(afterBorrowingValues[position]) !== expectedAfterBorrowing) {
           errors.push({
             type: 'wrong_after_borrowing',
             position: position,
             placeValue: placeValueName,
             message: `Wrong "After borrowing" value in ${placeValueName} place. When borrowing, ${digitA} becomes ${expectedAfterBorrowing}, not ${afterBorrowingValues[position]}.`
           });
         }
         
         // STEP 3: Find the leftmost position that can provide borrowing and handle each place value separately
         let borrowFromPosition = position + 1;
         let foundBorrowSource = false;
         
         while (borrowFromPosition < maxLength && !foundBorrowSource) {
           const leftDigitA = currentDigits[borrowFromPosition]; // Use current value
           
           if (leftDigitA > 0) {
             // Found a position that can provide borrowing
             foundBorrowSource = true;
             
             // STEP 3A: Check the borrowing source position FIRST (the one providing the borrow)
             let sourceMessage = `Since 1 is borrowed from ${getPlaceValueName(borrowFromPosition)} place to ${placeValueName} place, ${leftDigitA} becomes ${leftDigitA - 1} in ${getPlaceValueName(borrowFromPosition)} place`;
             let sourceDetails = [];
             
             const displayIndex = maxLength - (borrowFromPosition + 1);
             if (strikeoutValues[displayIndex] === undefined || strikeoutValues[displayIndex] === '') {
               sourceDetails.push(`Missing strikeout for ${leftDigitA}`);
             } else if (strikeoutValues[displayIndex] !== '/') {
               sourceDetails.push(`Wrong strikeout. You should write "/" to strike out ${leftDigitA}, not "${strikeoutValues[displayIndex]}"`);
             }
             
             if (afterReductionValues[borrowFromPosition] === undefined || afterReductionValues[borrowFromPosition] === '') {
               sourceDetails.push(`You need to write ${leftDigitA - 1} in the After Reduction field`);
             } else if (parseInt(afterReductionValues[borrowFromPosition]) !== (leftDigitA - 1)) {
               sourceDetails.push(`Wrong "After reduction" value. You wrote ${afterReductionValues[borrowFromPosition]}, but it should be ${leftDigitA - 1}`);
             }
             
             if (sourceDetails.length > 0) {
               errors.push({
                 type: 'source_after_reduction',
                 position: borrowFromPosition,
                 placeValue: getPlaceValueName(borrowFromPosition),
                 message: `${sourceMessage}. ${sourceDetails.join('. ')}.`
               });
             }
             
             // STEP 3B: Check intermediate zeros SECOND (these become 9 because of the borrowing above)
             for (let zeroPosition = position + 1; zeroPosition < borrowFromPosition; zeroPosition++) {
               const zeroDigit = currentDigits[zeroPosition]; // Use current value
               if (zeroDigit === 0) {
                 // Create separate error message for each zero position, clearly linking it to the borrowing
                 let zeroMessage = `In ${getPlaceValueName(zeroPosition)} place, because 1 is borrowed from ${getPlaceValueName(borrowFromPosition)} place, 0 becomes 9`;
                 let zeroDetails = [];
                 
                 if (afterReductionValues[zeroPosition] === undefined || afterReductionValues[zeroPosition] === '') {
                   zeroDetails.push(`You need to write 9 in the After Reduction field`);
                 } else if (parseInt(afterReductionValues[zeroPosition]) !== 9) {
                   zeroDetails.push(`Wrong value. You wrote ${afterReductionValues[zeroPosition]}, but it should be 9`);
                 }
                 
                 const zeroDisplayIndex = maxLength - (zeroPosition + 1);
                 if (strikeoutValues[zeroDisplayIndex] === undefined || strikeoutValues[zeroDisplayIndex] === '') {
                   zeroDetails.push(`Missing strikeout for 0`);
                 } else if (strikeoutValues[zeroDisplayIndex] !== '/') {
                   zeroDetails.push(`Wrong strikeout. You should write "/" to strike out 0, not "${strikeoutValues[zeroDisplayIndex]}"`);
                 }
                 
                 if (zeroDetails.length > 0) {
                   errors.push({
                     type: 'zero_after_reduction',
                     position: zeroPosition,
                     placeValue: getPlaceValueName(zeroPosition),
                     message: `${zeroMessage}. ${zeroDetails.join('. ')}.`
                   });
                 }
                 
                 // Update currentDigits array for this zero position
                 currentDigits[zeroPosition] = 9;
               }
             }
             
             // Update currentDigits array for this position
             currentDigits[borrowFromPosition] = leftDigitA - 1;
             
             break; // Found the borrowing source, no need to continue
           }
           
           borrowFromPosition++;
         }
       } else {
         // No borrowing needed at this position
         // Check for unnecessary borrowing values
         if (borrowValues[position] !== undefined && borrowValues[position] !== '') {
           errors.push({
             type: 'unnecessary_borrow',
             position: position,
             placeValue: placeValueName,
             message: `Unnecessary borrowing in ${placeValueName} place. Since no borrowing is needed, remove the borrow value.`
           });
         }
         
         if (afterBorrowingValues[position] !== undefined && afterBorrowingValues[position] !== '') {
           errors.push({
             type: 'unnecessary_after_borrowing',
             position: position,
             placeValue: placeValueName,
             message: `Unnecessary "After borrowing" value in ${placeValueName} place. Since no borrowing is needed, remove this value.`
           });
         }
       }
     }
     
     return { errors };
   };

  // Validate answer step by step (when answer is wrong)
  const validateAnswerStepByStep = () => {
    const a = parseInt(num1);
    const b = parseInt(num2);
    const expectedResult = a - b;
    const expectedResultString = expectedResult.toString();
    const userAnswerString = userAnswer.join('');
    
    let errors = [];
    let borrow = 0;
    
    // Process from right to left (ones place first) to find the first error
    for (let i = expectedResultString.length - 1; i >= 0; i--) {
      // Extract digits from right to left
      const positionFromRight = expectedResultString.length - 1 - i; // 0 = ones, 1 = tens, 2 = hundreds
      
      // Get the digit from user's answer (from right side)
      const userDigit = parseInt(userAnswerString.charAt(userAnswerString.length - 1 - positionFromRight) || '');
      
      // Get the digits from the numbers being subtracted
      const digitA = parseInt(a.toString().padStart(expectedResultString.length, '0')[i] || 0);
      const digitB = parseInt(b.toString().padStart(expectedResultString.length, '0')[i] || 0);
      
      // Check if user attempted this digit
      if (isNaN(userDigit)) {
        errors.push({
          type: 'unattempted',
          position: positionFromRight,
          placeValue: getPlaceValueName(positionFromRight),
          message: `You didn't attempt the ${getPlaceValueName(positionFromRight)} place`
        });
        break; // Stop at first error
      }
      
      // Apply borrowing if needed
      let currentDigitA = digitA - borrow;
      let newBorrow = 0;
      
      if (currentDigitA < digitB) {
        currentDigitA += 10;
        newBorrow = 1;
      }
      
      const expectedDigit = currentDigitA - digitB;
      
      // Check if user's digit matches expected
      if (userDigit !== expectedDigit) {
        errors.push({
          type: 'wrong_digit',
          position: positionFromRight,
          placeValue: getPlaceValueName(positionFromRight),
          expected: expectedDigit,
          actual: userDigit,
          digitA,
          digitB,
          borrow,
          currentDigitA,
          message: `Wrong digit in ${getPlaceValueName(positionFromRight)} place. Expected ${expectedDigit}, got ${userDigit}.`
        });
        break; // Stop at first error
      }
      
      borrow = newBorrow;
    }
    
    return { errors };
  };

  // Generate detailed error message with structured data for JSX rendering
  const generateDetailedErrorMessage = (validationSteps) => {
    if (validationSteps.errors.length === 0) {
      return { type: 'success', text: 'Correct! Well done! 🎉' };
    }
    
    return {
      type: 'error',
      errors: validationSteps.errors,
      correctAnswer: parseInt(num1) - parseInt(num2)
    };
  };

  // Get place value name
  const getPlaceValueName = (index) => {
    if (index === 0) return 'ones';
    if (index === 1) return 'tens';
    if (index === 2) return 'hundreds';
    if (index === 3) return 'thousands';
    if (index === 4) return 'ten thousands';
    if (index === 5) return 'hundred thousands';
    if (index === 6) return 'millions';
    return `${Math.pow(10, index)}s`;
  };

  // Render error message with proper styling
  const renderErrorMessage = (messageData) => {
    if (messageData.type === 'success') {
      return <div>{messageData.text}</div>;
    }
    
    if (messageData.type === 'error') {
      return (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#dc3545' }}>
            Incorrect answer. Here are the issues:
          </div>
          
          {messageData.errors.map((error, index) => (
            <div key={index} style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #dee2e6' }}>
              <div style={{ fontWeight: 'bold', color: '#dc3545', marginBottom: '5px' }}>
                {index + 1}. {error.message}
              </div>
              
              {error.type === 'wrong_digit' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  <div>Calculation: <span style={{ color: '#dc3545', fontWeight: 'bold' }}>{error.digitA} - {error.digitB}</span>
                    {error.borrow > 0 && <span style={{ color: '#dc3545', fontWeight: 'bold' }}> - {error.borrow} (borrow)</span>}
                    {` → `}
                    <span style={{ color: '#dc3545', fontWeight: 'bold' }}>{error.currentDigitA} - {error.digitB}</span>
                    <span style={{ color: '#dc3545', fontWeight: 'bold' }}> = {error.expected}</span>
                  </div>
                  <div style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    You should write {error.expected} in the {error.placeValue} place
                  </div>
                </div>
              )}
              
              {error.type === 'missing_borrow' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  This affects the next place value calculation
                </div>
              )}
              
              {error.type === 'wrong_borrow' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  This will cause errors in subsequent calculations
                </div>
              )}
              
              {error.type === 'unnecessary_borrow' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  Remove this borrowing as it's not needed
                </div>
              )}
              
              {error.type === 'missing_after_borrowing' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  <div>When borrowing occurs, you must show the borrowed value:</div>
                  <div style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    {error.originalDigit} → {error.expectedValue} (after borrowing)
                  </div>
                </div>
              )}
              
              {error.type === 'wrong_after_borrowing' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  <div>When borrowing occurs, the digit changes:</div>
                  <div style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    {error.originalDigit} → {error.expectedValue} (correct)
                  </div>
                  <div style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    You wrote: {error.originalDigit} → {error.actual} (incorrect)
                  </div>
                </div>
              )}
              
              {error.type === 'missing_after_reduction' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  <div>When borrowing occurs, you must show the reduced value:</div>
                  <div style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    {error.originalDigit} → {error.expectedValue} (after borrowing)
                  </div>
                </div>
              )}
              
              {error.type === 'wrong_after_reduction' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  <div>When borrowing occurs, the digit is reduced:</div>
                  <div style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    {error.originalDigit} → {error.expectedValue} (correct)
                  </div>
                  <div style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    You wrote: {error.originalDigit} → {error.actual} (incorrect)
                  </div>
                </div>
              )}
              
              {error.type === 'missing_strikeout' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  <div>When borrowing occurs, you must strike out the original digit:</div>
                  <div style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    Strike out {error.expectedValue} to show borrowing
                  </div>
                </div>
              )}
              
              {error.type === 'wrong_strikeout' && (
                <div style={{ marginLeft: '20px', color: '#6c757d' }}>
                  <div>You struck out the wrong digit:</div>
                  <div style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    Should strike out: {error.expected} (correct)
                  </div>
                  <div style={{ color: '#dc3545', fontWeight: 'bold' }}>
                    You struck out: {error.actual} (incorrect)
                  </div>
                </div>
              )}
            </div>
          ))}
          
          <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '6px', fontWeight: 'bold', textAlign: 'center' }}>
            The correct answer is {messageData.correctAnswer}.
          </div>
        </div>
      );
    }
    
    // Fallback for plain text messages
    return <div style={{ whiteSpace: 'pre-line' }}>{messageData}</div>;
  };

  // Show solution with step-by-step explanation
  const showSolutionSteps = () => {
    const { steps, result } = getSolutionSteps();
    setSolutionSteps({ steps, result });
    setShowSolution(true);
    
    // Track solution view
    setSessionStats(prev => ({
      ...prev,
      totalShownSolutions: prev.totalShownSolutions + 1
    }));
    
    // Track metrics for show solution
    metricsTracker.trackShowSolution('subtraction', gradeLevel);
    
    // Disable Show Solution button after use
    setButtonsState(prev => ({
      ...prev,
      showSolutionDisabled: true
    }));
  };

  // Calculate the correct answer and steps with borrowing
  const getSolutionSteps = () => {
    const a = parseInt(num1);
    const b = parseInt(num2);
    const result = a - b;
    
    const aDigits = a.toString().split('').reverse();
    const bDigits = b.toString().split('').reverse();
    const maxLength = Math.max(aDigits.length, bDigits.length);
    
    let steps = [];
    let borrow = 0;
    
    for (let i = 0; i < maxLength; i++) {
      const digitA = parseInt(aDigits[i] || 0);
      const digitB = parseInt(bDigits[i] || 0);
      
      // Apply borrowing if needed
      let currentDigitA = digitA - borrow;
      let newBorrow = 0;
      
      if (currentDigitA < digitB) {
        currentDigitA += 10;
        newBorrow = 1;
      }
      
      const resultDigit = currentDigitA - digitB;
      
      let placeValue = '';
      if (i === 0) placeValue = 'ones';
      else if (i === 1) placeValue = 'tens';
      else if (i === 2) placeValue = 'hundreds';
      else if (i === 3) placeValue = 'thousands';
      else if (i === 4) placeValue = 'ten thousands';
      else if (i === 5) placeValue = 'hundred thousands';
      else if (i === 6) placeValue = 'millions';
      else placeValue = `${Math.pow(10, i)}s`;
      
      steps.push({
        position: i + 1,
        digitA,
        digitB,
        borrow: borrow,
        currentDigitA,
        resultDigit,
        newBorrow: newBorrow,
        placeValue,
        needsBorrow: digitA < digitB
      });
      
      borrow = newBorrow;
    }
    
    return { steps, result };
  };

  // Improved keyboard navigation
  const handleKeyDown = (e, currentIndex, type) => {
    if (e.key === 'Enter') {
      validateAnswer();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const inputs = document.querySelectorAll('input[data-index]');
      const currentInputIndex = Array.from(inputs).findIndex(input => input === e.target);
      
      if (e.shiftKey) {
        // Shift+Tab - move to previous input
        if (currentInputIndex > 0) {
          inputs[currentInputIndex - 1].focus();
        }
      } else {
        // Tab - move to next input
        if (currentInputIndex < inputs.length - 1) {
          inputs[currentInputIndex + 1].focus();
        }
      }
    }
  };

  // Hide keyboard when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.virtual-keyboard') && 
          !event.target.closest('.digit-input') && 
          !event.target.closest('.after-borrowing-input') &&
          !event.target.closest('.after-reduction-input') &&
          !event.target.closest('.strikeout-input')) {
        setKeyboardVisible(false);
        setActiveInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generate initial problem on component mount
  useEffect(() => {
    generateProblem(true); // Pass true for initial load
  }, [gradeLevel]);

  const expectedLength = solutionSteps ? solutionSteps.result.toString().length : 0;
  
  // Calculate alignment for proper place value positioning
  const maxLength = Math.max(num1.length, num2.length, expectedLength);
  
  // Pad numbers with spaces for proper alignment
  const padNumber = (num, length) => {
    return num.padStart(length, ' ');
  };

  const { papersSaved, totalLines } = calculatePaperSavings();

  return (
    <div>
      {/* Compact Header */}
      <div style={{ marginBottom: '15px' }}>
        <h2 style={{ margin: '0', fontSize: '1.6rem', color: '#333' }}>Subtraction Practice</h2>
      </div>

      {/* Compact Controls Row */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '15px', 
        marginBottom: '15px',
        flexWrap: 'wrap'
      }}>
           <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '0.9rem', fontWeight: '500' }}>Level:</label>
          <select 
            value={gradeLevel} 
            onChange={(e) => {
              setGradeLevel(e.target.value);
              // Track level selection when user clicks dropdown
              metricsTracker.trackLevelSelected('subtraction', e.target.value);
            }}
            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            {Array.from({ length: 8 }, (_, i) => i + 1).map(level => (
              <option 
                key={level} 
                value={level.toString()}
                disabled={level > 3}
                style={{ color: level > 3 ? '#999' : 'inherit' }}
              >
                {level > 3 ? `Level ${level} (Upto ${level + 1} digits) - Upgrade to Pro Edition` : `Level ${level} (Upto ${level + 1} digits)`}
              </option>
            ))}
          </select>
        </div>
        {parseInt(gradeLevel) > 3 && (
          <div style={{ 
            marginTop: '8px', 
            padding: '8px 12px', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffeaa7', 
            borderRadius: '4px',
            fontSize: '14px',
            color: '#856404'
          }}>
            ⚠️ Upgrade to Pro Edition to support Level {gradeLevel} (Upto {parseInt(gradeLevel) + 1} digits)
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '0.9rem', fontWeight: '500' }}>
            <input 
              type="checkbox" 
              checked={useCustomNumbers}
              onChange={(e) => setUseCustomNumbers(e.target.checked)}
              style={{ marginRight: '5px' }}
            />
            Custom Numbers
          </label>
        </div>

          {useCustomNumbers && (
          <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                placeholder="1st (larger)"
                value={customNumbers.num1}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 4) {
                    setCustomNumbers(prev => ({ ...prev, num1: value }));
                  }
                }}
                style={{ width: '100px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              <input
                type="number"
                placeholder="2nd (smaller)"
                value={customNumbers.num2}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 4) {
                    setCustomNumbers(prev => ({ ...prev, num2: value }));
                  }
                }}
                style={{ width: '100px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            {(customNumbers.num1.length > 4 || customNumbers.num2.length > 4) && (
              <div style={{ 
                fontSize: '0.8rem', 
                color: '#e74c3c', 
                marginTop: '4px',
                padding: '4px 8px',
                backgroundColor: '#fdf2f2',
                borderRadius: '4px',
                border: '1px solid #fecaca'
              }}>
                ⚠️ Upgrade to Pro Edition to use numbers with more than 4 digits
            </div>
          )}
        </div>
        )}

        <button className="btn btn-primary" onClick={generateProblem} style={{ padding: '6px 12px' }}>
          Generate Problem
        </button>
      </div>

      {/* Compact Progress Display */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '25px',
        marginBottom: '15px',
        padding: '8px 15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '6px',
        border: '1px solid #dee2e6',
        fontSize: '0.9rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ color: '#6c757d' }}>Attempted:</span>
          <span style={{ fontWeight: 'bold', color: '#667eea' }}>{sessionStats.totalAttempted}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ color: '#6c757d' }}>Correct:</span>
          <span style={{ fontWeight: 'bold', color: '#28a745' }}>{sessionStats.totalCorrect}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ color: '#6c757d' }}>Solutions:</span>
          <span style={{ fontWeight: 'bold', color: '#ffc107' }}>{sessionStats.totalShownSolutions}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ color: '#6c757d' }}>Success:</span>
          <span style={{ fontWeight: 'bold', color: sessionStats.totalAttempted > 0 ? '#28a745' : '#6c757d' }}>
            {sessionStats.totalAttempted > 0 ? Math.round((sessionStats.totalCorrect / sessionStats.totalAttempted) * 100) : 0}%
          </span>
        </div>
      </div>

      {/* Question Display */}
      <div className="question-number">
        {num1}-{num2}=?
      </div>

      {/* Practice Layout - Problem and Solution Side by Side */}
      <div className="practice-layout">
        {/* Problem Section */}
        <div className="problem-section">
          {/* Math Problem */}
          <div className="math-problem" ref={mathProblemRef} style={{ position: 'relative' }}>
                         {/* Grid container for perfect alignment */}
             <div style={{ 
               display: 'grid', 
               gridTemplateColumns: `40px repeat(${Math.max(maxLength, calculateAnswerLength())}, 40px) auto`,
               gap: '0px',
               alignItems: 'center'
             }}>
                               {/* After Reduction row */}
                <div></div> {/* Empty cell for operator space */}
                 {Array.from({ length: Math.max(maxLength, calculateAnswerLength()) }, (_, index) => {
                   // Convert left-to-right index to right-to-left index for proper place value mapping
                   const rightToLeftIndex = Math.max(maxLength, calculateAnswerLength()) - 1 - index;
                   return (
                  <div key={`after-reduction-${index}`} style={{ textAlign: 'center' }}>
                    {index < Math.max(maxLength, calculateAnswerLength()) - 1 && (
                      <input
                        type="text"
                        className="after-reduction-input"
                           value={afterReductionValues[rightToLeftIndex] || ''}
                           onChange={(e) => handleAfterReductionChange(rightToLeftIndex, e.target.value)}
                           onFocus={(e) => handleInputFocus(rightToLeftIndex, 'afterReduction')}
                        onBlur={handleInputBlur}
                           onKeyDown={(e) => handleKeyDown(e, rightToLeftIndex, 'afterReduction')}
                           data-index={`${rightToLeftIndex}`}
                        data-type="afterReduction"
                        placeholder=""
                        maxLength={1}
                        style={{
                          width: '30px',
                          height: '25px',
                          fontSize: '0.8rem',
                          textAlign: 'center',
                          border: 'none',
                          borderBottom: '2px solid #0066cc',
                          borderRadius: '0',
                          backgroundColor: '#e6f3ff',
                          color: '#0066cc',
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                  </div>
                   );
                 })}
                <div style={{ fontSize: '0.8rem', color: '#0066cc', fontWeight: 'bold' }}>
                  After Reduction
                </div>

                {/* After Borrowing row */}
                <div></div> {/* Empty cell for operator space */}
                 {Array.from({ length: Math.max(maxLength, calculateAnswerLength()) }, (_, index) => {
                   // Convert left-to-right index to right-to-left index for proper place value mapping
                   const rightToLeftIndex = Math.max(maxLength, calculateAnswerLength()) - 1 - index;
                   return (
                  <div key={`after-borrowing-${index}`} style={{ textAlign: 'center' }}>
                    {index < Math.max(maxLength, calculateAnswerLength()) && (
                      <input
                        type="text"
                        className="after-borrowing-input"
                           value={afterBorrowingValues[rightToLeftIndex] || ''}
                           onChange={(e) => handleAfterBorrowingChange(rightToLeftIndex, e.target.value)}
                           onFocus={(e) => handleInputFocus(rightToLeftIndex, 'afterBorrowing')}
                        onBlur={handleInputBlur}
                           onKeyDown={(e) => handleKeyDown(e, rightToLeftIndex, 'afterBorrowing')}
                           data-index={`${rightToLeftIndex}`}
                        data-type="afterBorrowing"
                        placeholder=""
                        maxLength={2}
                        style={{
                          width: '35px',
                          height: '25px',
                          fontSize: '0.8rem',
                          textAlign: 'center',
                          border: 'none',
                          borderBottom: '2px solid #ff8c00',
                          borderRadius: '0',
                          backgroundColor: '#ffe6cc',
                          color: '#ff8c00',
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                  </div>
                   );
                 })}
                <div style={{ fontSize: '0.8rem', color: '#ff8c00', fontWeight: 'bold' }}>
                  After Borrowing
                </div>

                 {/* Borrow row - NEW ADDITION */}
                 <div></div> {/* Empty cell for operator space */}
                 {Array.from({ length: Math.max(maxLength, calculateAnswerLength()) }, (_, index) => {
                   // Convert left-to-right index to right-to-left index for proper place value mapping
                   const rightToLeftIndex = Math.max(maxLength, calculateAnswerLength()) - 1 - index;
                   return (
                     <div key={`borrow-${index}`} style={{ textAlign: 'center' }}>
                       {index < Math.max(maxLength, calculateAnswerLength()) && (
                         <input
                           type="text"
                           className="borrow-input"
                           value={borrowValues[rightToLeftIndex] || ''}
                           onChange={(e) => handleBorrowChange(rightToLeftIndex, e.target.value)}
                           onFocus={(e) => handleInputFocus(rightToLeftIndex, 'borrow')}
                           onBlur={handleInputBlur}
                           onKeyDown={(e) => handleKeyDown(e, rightToLeftIndex, 'borrow')}
                           data-index={`${rightToLeftIndex}`}
                           data-type="borrow"
                           placeholder=""
                           maxLength={1}
                           style={{
                             width: '35px',
                             height: '25px',
                             fontSize: '0.8rem',
                             textAlign: 'center',
                             border: 'none',
                             borderBottom: '2px solid #ff6b35',
                             borderRadius: '0',
                             backgroundColor: '#ffe8d6',
                             color: '#ff6b35',
                           fontWeight: 'bold'
                           }}
                         />
                       )}
                     </div>
                   );
                 })}
                 <div style={{ fontSize: '0.8rem', color: '#ff6b35', fontWeight: 'bold' }}>
                   Borrow
                </div>

               {/* First number row with strikeout functionality */}
               <div></div> {/* Empty cell for operator space */}
               {padNumber(num1, Math.max(maxLength, calculateAnswerLength())).split('').map((digit, index) => (
                 <div key={`num1-container-${index}`} style={{ 
                   width: '40px', 
                   height: '50px',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   position: 'relative'
                 }}>
                   <input
                     key={`num1-${index}`}
                     type="text"
                     className="digit-input"
                     value={digit === ' ' ? '' : digit}
                     readOnly
                     style={{ 
                       backgroundColor: '#e8f4fd',
                       width: '40px',
                       height: '50px',
                       textAlign: 'center',
                       fontSize: '1.5rem',
                       fontWeight: 'bold',
                       border: '2px solid #ddd',
                       borderRadius: '4px',
                       position: 'relative',
                       zIndex: 1
                     }}
                   />
                   {/* Strikeout overlay */}
                   {strikeoutValues[index] === '/' && (
                     <div style={{
                       position: 'absolute',
                       top: '50%',
                       left: '50%',
                       transform: 'translate(-50%, -50%)',
                       width: '45px',
                       height: '3px',
                       backgroundColor: '#dc3545',
                       transform: 'translate(-50%, -50%) rotate(-45deg)',
                       zIndex: 2,
                       pointerEvents: 'none'
                     }}></div>
                   )}
                   {/* Strikeout input */}
                   <input
                     type="text"
                     className="strikeout-input"
                     value={strikeoutValues[index] || ''}
                     onChange={(e) => handleStrikeoutChange(index, e.target.value)}
                     onFocus={(e) => handleInputFocus(index, 'strikeout')}
                     onBlur={handleInputBlur}
                     onKeyDown={(e) => handleKeyDown(e, index, 'strikeout')}
                     data-index={`${index}`}
                     data-type="strikeout"
                     placeholder=""
                     maxLength={1}
                     style={{
                       position: 'absolute',
                       top: '2px',
                       right: '2px',
                       width: '15px',
                       height: '15px',
                       fontSize: '0.6rem',
                       textAlign: 'center',
                       border: '1px solid #dc3545',
                       borderRadius: '2px',
                       backgroundColor: '#f8d7da',
                       color: '#dc3545',
                       zIndex: 3
                     }}
                   />
                 </div>
               ))}
               <div></div> {/* Empty cell for labels */}

              {/* Second number row with operator */}
              <span className="operator" style={{ textAlign: 'center' }}>-</span>
              {padNumber(num2, Math.max(maxLength, calculateAnswerLength())).split('').map((digit, index) => (
                <input
                  key={`num2-${index}`}
                  type="text"
                  className="digit-input"
                  value={digit === ' ' ? '' : digit}
                  readOnly
                  style={{ 
                    backgroundColor: '#e8f4fd',
                    width: '40px',
                    height: '50px',
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    border: '2px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              ))}
              <div></div> {/* Empty cell for labels */}

              {/* Line row */}
              <div></div> {/* Empty cell for operator space */}
              {Array.from({ length: Math.max(maxLength, calculateAnswerLength()) }, (_, index) => (
                <div key={`line-${index}`} style={{ 
                  height: '2px', 
                  backgroundColor: '#333',
                  margin: '10px 0'
                }}></div>
              ))}
              <div></div> {/* Empty cell for labels */}

              {/* Answer row */}
              <div></div> {/* Empty cell for operator space */}
              {Array.from({ length: calculateAnswerLength() }, (_, index) => (
                <div key={`answer-container-${index}`} style={{ 
                  width: '40px', 
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}>
                  <input
                    key={`answer-${index}`}
                    type="text"
                    className={`digit-input ${isCorrect !== null ? 
                      (userAnswer[index] === (parseInt(num1) - parseInt(num2)).toString().padStart(calculateAnswerLength(), '0')[index] ? 'correct' : 'incorrect') : ''}`}
                    value={userAnswer[index] || ''}
                    onChange={(e) => handleDigitChange(index, e.target.value)}
                    onFocus={(e) => handleInputFocus(index, 'answer')}
                    onBlur={handleInputBlur}
                    onKeyDown={(e) => handleKeyDown(e, index, 'answer')}
                                         data-index={`${index}`}
                    data-type="answer"
                    maxLength={1}
                    placeholder=""
                    style={{
                      width: '20px',
                      height: '30px',
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      border: 'none',
                      borderRadius: '0',
                      backgroundColor: 'transparent',
                      transition: 'border-color 0.3s ease',
                      outline: 'none'
                    }}
                  />
                  {/* Dash underline */}
                  <div style={{
                    position: 'absolute',
                    bottom: '5px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '25px',
                    height: '3px',
                    backgroundColor: '#333',
                    borderRadius: '1px'
                  }}></div>
                </div>
              ))}
              <div></div> {/* Empty cell for labels */}
            </div>

            {/* Virtual Keyboard */}
            <VirtualKeyboard
              onKeyPress={handleVirtualKeyPress}
              onBackspace={handleVirtualBackspace}
              onClear={handleVirtualClear}
              position={keyboardPosition}
              isVisible={keyboardVisible}
            />
          </div>

          {/* Result Message */}
          {message && (
            <div className={`result ${isCorrect ? 'success' : 'error'}`}>
              {isCorrect ? (
                <div>{message}</div>
              ) : (
                <div style={{ textAlign: 'left' }}>
                  {renderErrorMessage(message)}
                </div>
              )}
            </div>
          )}

          {/* Controls */}
          <div className="controls">
            <button 
              className="btn btn-success" 
              onClick={validateAnswer}
              disabled={buttonsState.checkAnswerDisabled}
              style={{
                opacity: buttonsState.checkAnswerDisabled ? 0.6 : 1,
                cursor: buttonsState.checkAnswerDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              {buttonsState.checkAnswerDisabled ? 'Already Checked' : 'Check Answer'}
            </button>
            <button 
              className="btn btn-warning" 
              onClick={showSolutionSteps}
              disabled={buttonsState.showSolutionDisabled}
              style={{
                opacity: buttonsState.showSolutionDisabled ? 0.6 : 1,
                cursor: buttonsState.showSolutionDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              Show Solution
            </button>
          </div>
        </div>

        {/* Solution Section */}
        {showSolution && solutionSteps && (
          <div className="solution-section">
            <h3>Step-by-Step Solution</h3>
            <div className="solution-steps">
              {solutionSteps.steps.map((step, index) => (
                <div key={index} style={{ marginBottom: '15px', padding: '10px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                  <strong style={{ color: '#667eea' }}>Step {step.position} ({step.placeValue} place):</strong>
                  <div style={{ marginTop: '5px', fontFamily: 'monospace' }}>
                    {step.needsBorrow ? (
                      <>
                        <span style={{ color: '#333', backgroundColor: '#e8f4fd', padding: '2px 4px', borderRadius: '3px' }}>{step.digitA}</span> - 
                        <span style={{ color: '#333', backgroundColor: '#e8f4fd', padding: '2px 4px', borderRadius: '3px' }}>{step.digitB}</span>
                        {step.borrow > 0 && ` - `}
                        {step.borrow > 0 && <span style={{ color: '#e67e22', backgroundColor: '#fff3e0', padding: '2px 4px', borderRadius: '3px' }}>{step.borrow} (borrow)</span>}
                        {` → `}
                        <span style={{ color: '#e67e22', backgroundColor: '#fff3e0', padding: '2px 4px', borderRadius: '3px' }}>{step.currentDigitA}</span> - 
                        <span style={{ color: '#333', backgroundColor: '#e8f4fd', padding: '2px 4px', borderRadius: '3px' }}>{step.digitB}</span>
                        {` = `}
                        <span style={{ color: '#155724', backgroundColor: '#d4edda', padding: '2px 4px', borderRadius: '3px' }}>{step.resultDigit}</span>
                        {` (write ${step.resultDigit}, borrow ${step.newBorrow} for next place)`}
                      </>
                    ) : (
                      <>
                        <span style={{ color: '#333', backgroundColor: '#e8f4fd', padding: '2px 4px', borderRadius: '3px' }}>{step.digitA}</span> - 
                        <span style={{ color: '#333', backgroundColor: '#e8f4fd', padding: '2px 4px', borderRadius: '3px' }}>{step.digitB}</span>
                        {step.borrow > 0 && ` - `}
                        {step.borrow > 0 && <span style={{ color: '#e67e22', backgroundColor: '#fff3e0', padding: '2px 4px', borderRadius: '3px' }}>{step.borrow} (borrow)</span>}
                        {` = `}
                        <span style={{ color: '#155724', backgroundColor: '#d4edda', padding: '2px 4px', borderRadius: '3px' }}>{step.resultDigit}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#667eea', color: 'white', borderRadius: '6px', fontWeight: 'bold', textAlign: 'center' }}>
                Final Answer: {solutionSteps.result}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subtraction; 